import type { MappedTuple } from "@/interfaces/estimate-detail";
import type { Category } from "../Lines";
import { useState } from "react";
import { useCallback } from "react";
import { isItemArchived } from "@rebuild/shared";
export interface DuplicateCheckResult {
  isDuplicate: boolean;
  location: "main" | "recommended" | "issues" | null;
  duplicateItem?: MappedTuple;
}

interface UseLineItemDuplicateCheckProps {
  filterLines: MappedTuple[];
  itemCategoryTab: Record<Category, MappedTuple[]>;
  onRemoveRecommendation?: (item: MappedTuple) => void;
}

const initialDuplicateResult: DuplicateCheckResult = {
  isDuplicate: false,
  location: null,
};

export const useLineItemDuplicateCheck = ({ filterLines, itemCategoryTab }: UseLineItemDuplicateCheckProps) => {
  const [duplicateError, setDuplicateError] = useState<string>("");
  const [duplicateResult, setDuplicateResult] = useState<DuplicateCheckResult>(initialDuplicateResult);

  const checkForDuplicateLineItem = useCallback(
    (newLineItem: string, currentPermutation?: string): DuplicateCheckResult => {
      const mainDuplicate = filterLines.find(
        (existing) =>
          existing.currentItem.lineItem.trim().toLowerCase() === newLineItem.trim().toLowerCase() &&
          existing.currentItem.lineItem.trim() !== currentPermutation?.trim() &&
          !isItemArchived(existing.currentItem),
      );

      let duplicateResult: DuplicateCheckResult | null = null;

      if (mainDuplicate) {
        duplicateResult = {
          isDuplicate: true,
          location: "main",
          duplicateItem: mainDuplicate,
        };
        setDuplicateResult(duplicateResult);
        return duplicateResult;
      }

      // Check in recommendations
      const recommendedDuplicate = itemCategoryTab.recommended.find(
        (existing) =>
          existing.currentItem.lineItem.trim().toLowerCase() === newLineItem.trim().toLowerCase() &&
          existing.currentItem.lineItem.trim() !== currentPermutation?.trim() &&
          !isItemArchived(existing.currentItem),
      );

      if (recommendedDuplicate) {
        duplicateResult = {
          isDuplicate: true,
          location: "recommended",
          duplicateItem: recommendedDuplicate,
        };
        setDuplicateResult(duplicateResult);
        return duplicateResult;
      }

      // Check in issues
      const issueDuplicate = itemCategoryTab.issues.find(
        (existing) =>
          existing.currentItem.lineItem.trim().toLowerCase() === newLineItem.trim().toLowerCase() &&
          existing.currentItem.lineItem.trim() !== currentPermutation?.trim() &&
          !isItemArchived(existing.currentItem),
      );

      if (issueDuplicate) {
        duplicateResult = {
          isDuplicate: true,
          location: "issues",
          duplicateItem: issueDuplicate,
        };
        setDuplicateResult(duplicateResult);
        return duplicateResult;
      }

      if (!duplicateResult) {
        duplicateResult = {
          isDuplicate: false,
          location: null,
        };
        setDuplicateResult(duplicateResult);
        return duplicateResult;
      }

      return duplicateResult;
    },
    [filterLines, itemCategoryTab, setDuplicateResult],
  );

  const handleDuplicateCheck = useCallback(
    (newLineItem: string, currentPermutation?: string): DuplicateCheckResult => {
      const issueMessage = "This line item duplicates an existing issue - are you sure you want to add it?";
      const mainMessage = "There is already an exact match for this line item in this room - are you sure you want to add it?";

      const result = checkForDuplicateLineItem(newLineItem, currentPermutation);

      if (result.isDuplicate) {
        setDuplicateError(result.location === "main" ? mainMessage : result.location === "recommended" ? "" : issueMessage);
      } else {
        setDuplicateError("");
      }

      return result;
    },
    [checkForDuplicateLineItem],
  );

  const clearError = useCallback(() => {
    setDuplicateError("");
    setDuplicateResult(initialDuplicateResult);
  }, []);

  return {
    handleDuplicateCheck,
    duplicateError,
    duplicateResult,
    clearError,
  };
};
